/**
 * ProjectGeneralSettings
 * Project name, slug, description, type, status — extracted from ProjectSettingsPage.
 */

import { useState, useEffect, useCallback } from 'react';
import { useProject } from '../../contexts/ProjectContext';
import { projectsApi, type UpdateProjectRequest, type ProjectType, type ProjectStatus } from '../../api/projects';
import { Panel, PanelHeader, PanelBody } from '../shared/Panel';

export function ProjectGeneralSettings() {
  const { project, refreshProjects } = useProject();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<ProjectType>('service');
  const [status, setStatus] = useState<ProjectStatus>('active');
  const [issuePrefix, setIssuePrefix] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (project) {
      setName(project.name);
      setDescription(project.description || '');
      setType(project.type);
      setStatus(project.status);
      setIssuePrefix(project.issuePrefix || '');
    }
  }, [project]);

  const handleSave = useCallback(async () => {
    if (!project || !name.trim()) return;
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const updateData: UpdateProjectRequest = {
        name: name.trim(),
        type,
        status,
        ...(issuePrefix.trim() && { issuePrefix: issuePrefix.trim().toUpperCase() }),
      };
      await projectsApi.update(project.id, updateData);
      await refreshProjects();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to update project:', err);
      setSaveError(err instanceof Error ? err.message : 'Failed to update project');
    } finally {
      setIsSaving(false);
    }
  }, [project, name, type, status, refreshProjects]);

  if (!project) {
    return (
      <Panel>
        <PanelBody>
          <div className="text-center py-8 text-muted text-sm">
            Select a project to view its settings.
          </div>
        </PanelBody>
      </Panel>
    );
  }

  return (
    <div className="space-y-6">
      <Panel>
        <PanelHeader title="General" />
        <PanelBody>
          <div className="space-y-5">
            {saveSuccess && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                Project settings saved successfully.
              </div>
            )}
            {saveError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                {saveError}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-medium text-text mb-1">
                  Project Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-line rounded-lg text-sm focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none transition-colors"
                  placeholder="My Project"
                />
              </div>
              <div>
                <label className="block text-[12px] font-medium text-text mb-1">
                  Slug
                </label>
                <input
                  type="text"
                  value={project.slug}
                  disabled
                  className="w-full px-3 py-2 border border-line rounded-lg bg-surface-muted text-subtle text-sm cursor-not-allowed font-mono"
                />
                <p className="mt-1 text-[11px] text-subtle">Slug cannot be changed</p>
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-medium text-text mb-1">
                Issue Prefix
              </label>
              <input
                type="text"
                value={issuePrefix}
                onChange={(e) => setIssuePrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))}
                className="w-full px-3 py-2 border border-line rounded-lg text-sm focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none transition-colors font-mono uppercase"
                placeholder="e.g. CALC"
                maxLength={10}
              />
              <p className="mt-1 text-[11px] text-subtle">
                Used in issue IDs (e.g. {issuePrefix || 'CALC'}-001). 2-10 uppercase letters/numbers.
              </p>
            </div>

            <div>
              <label className="block text-[12px] font-medium text-text mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-line rounded-lg text-sm focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none resize-none transition-colors"
                placeholder="Optional project description..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-medium text-text mb-1">Type</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as ProjectType)}
                  className="w-full px-3 py-2 border border-line rounded-lg text-sm focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none bg-white transition-colors"
                >
                  <option value="service">Service</option>
                  <option value="app">App</option>
                  <option value="library">Library</option>
                </select>
              </div>
              <div>
                <label className="block text-[12px] font-medium text-text mb-1">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as ProjectStatus)}
                  className="w-full px-3 py-2 border border-line rounded-lg text-sm focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none bg-white transition-colors"
                >
                  <option value="setup">Setup</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={handleSave}
                disabled={isSaving || !name.trim()}
                className="px-4 py-2 bg-coral-500 text-white text-sm font-medium rounded-lg hover:bg-coral-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </PanelBody>
      </Panel>

      {/* Metadata */}
      <div className="flex gap-6 text-[11px] text-subtle px-1">
        <span>Created: {new Date(project.createdAt).toLocaleDateString()}</span>
        <span>Updated: {new Date(project.updatedAt).toLocaleDateString()}</span>
        <span className="font-mono">ID: {project.id}</span>
      </div>
    </div>
  );
}
